import instance from '../../../api/axios'
import {
  CreateWhiteboard,
  Whiteboard,
  WhiteboardFolder,
  WhiteboardRaw,
} from '../types/whiteboard'

const parseWhiteboard = (whiteboard: WhiteboardRaw): Whiteboard => ({
  ...whiteboard,
  excalidrawElement: whiteboard.excalidrawElement
    ? JSON.parse(whiteboard.excalidrawElement)
    : [],
  excalidrawFiles: whiteboard.excalidrawFiles
    ? JSON.parse(whiteboard.excalidrawFiles)
    : undefined,
})

export const loadWhiteboards = async (): Promise<Whiteboard[]> => {
  const response = await instance.get<WhiteboardRaw[]>('/whiteboards')

  return response.data
    ?.map(parseWhiteboard)
    .sort((a: Whiteboard, b: Whiteboard) =>
      a.createdAt.localeCompare(b.createdAt),
    )
}

export const loadWhiteboardsByFolder = async (
  folderId?: string,
): Promise<Whiteboard[]> => {
  const response = await instance.get<WhiteboardRaw[]>(
    '/whiteboards/folder-items',
    {
      params: folderId ? { folderId } : undefined,
    },
  )

  return response.data
    ?.map(parseWhiteboard)
    .sort((a: Whiteboard, b: Whiteboard) =>
      a.createdAt.localeCompare(b.createdAt),
    )
}

export const loadWhiteboardFolders = async (
  parentId?: string,
): Promise<WhiteboardFolder[]> => {
  const response = await instance.get<WhiteboardFolder[]>(
    '/whiteboards/folders',
    {
      params: parentId ? { parentId } : undefined,
    },
  )

  return response.data || []
}

export const loadAllWhiteboardFolders = async (): Promise<
  WhiteboardFolder[]
> => {
  const response = await instance.get<WhiteboardFolder[]>(
    '/whiteboards/folders/all',
  )
  return response.data || []
}

export const createWhiteboardFolder = async (payload: {
  name: string
  parentId?: string
  isPrivate?: boolean
}): Promise<WhiteboardFolder> => {
  const response = await instance.post<WhiteboardFolder>(
    '/whiteboards/folders',
    payload,
  )
  return response.data
}

export const updateWhiteboardFolder = async (
  id: string,
  data: Partial<WhiteboardFolder>,
): Promise<WhiteboardFolder> => {
  const response = await instance.put<WhiteboardFolder>(
    `/whiteboards/folders/${id}`,
    data,
  )
  return response.data
}

export const removeWhiteboardFolder = async (id: string): Promise<string> => {
  const response = await instance.delete(`/whiteboards/folders/${id}`)
  return response.data
}

export const loadWhiteboard = async (id: string): Promise<Whiteboard> => {
  const response = await instance.get<WhiteboardRaw>(`/whiteboards/${id}`)

  return response.data && parseWhiteboard(response.data)
}

export const saveWhiteboard = async (
  createWhiteboard: CreateWhiteboard,
): Promise<Whiteboard | undefined> => {
  const response = await instance.post<WhiteboardRaw>(
    '/whiteboards',
    createWhiteboard,
  )

  const createdDashboard = response.data
    ? parseWhiteboard(response.data)
    : undefined

  return createdDashboard
}

export const modifyWhiteboard = async (
  whiteboard: Whiteboard,
): Promise<Whiteboard> => {
  const response = await instance.put<WhiteboardRaw>('/whiteboards', {
    ...whiteboard,
    excalidrawElement: JSON.stringify(whiteboard.excalidrawElement),
    excalidrawFiles: whiteboard.excalidrawFiles
      ? JSON.stringify(whiteboard.excalidrawFiles)
      : null,
  })

  return response.data && parseWhiteboard(response.data)
}

export const removeWhiteboard = async (id: string): Promise<string> => {
  const response = await instance.delete(`/whiteboards/${id}`)
  return response.data
}
